// F1 Page Template Component
import React, { useEffect } from 'react';
import { useF1Championship } from '../../hooks/useF1Championship';
import Header from '../modules/HeaderModule';
import F1Breadcrumb from './F1Breadcrumb';
import LoadingSpinner from '../LoadingSpinner';

const F1PageTemplate = ({ 
  title, 
  subtitle, 
  summary, 
  children,
  stats = [],
  sections = []
}) => {
  const { data, loading, error } = useF1Championship();

  // Set document title to match page title
  useEffect(() => {
    document.title = title;
    document.querySelector('meta[name="description"]').content = subtitle;
  }, [title, subtitle]);

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message={`Loading ${title} Data...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Data</div>
          <div className="theme-secondary">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg-secondary font-Titillium">
      <F1Breadcrumb currentPage={title} />
      <div className="max-w-7xl mx-auto px-4">
        <Header data={{
          title,
          subtitle,
          summary
        }} />

        {/* Stats Section */}
        {stats.length > 0 && (
          <div className="theme-bg rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold theme-primary mb-6">Statistics Overview</h2>
            {Array.isArray(stats[0]) ? (
              // Grouped stats
              stats.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-6 last:mb-0">
                  <h3 className="text-lg font-semibold theme-primary mb-4">{group[0].group || 'Group'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {group.map((stat, statIndex) => (
                      <div key={statIndex} className="text-center">
                        <div className={`text-3xl font-bold ${stat.color || 'theme-primary'} mb-2`}>
                          {stat.value}
                        </div>
                        <div className="text-sm theme-secondary">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              // Flat stats (backward compatibility)
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className={`text-3xl font-bold ${stat.color || 'theme-primary'} mb-2`}>
                      {stat.value}
                    </div>
                    <div className="text-sm theme-secondary">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dynamic Sections */}
        {sections.map((section, index) => (
          <div key={index} className="theme-bg rounded-lg shadow-md p-8 mb-8">
            <h3 className="text-xl font-semibold theme-primary mb-4">{section.title}</h3>
            {section.content}
          </div>
        ))}

        {/* Custom Content */}
        {children}
      </div>
    </div>
  );
};

export default F1PageTemplate;
